/**
 * Data facade — the only place that talks to the backend.
 *
 * `loadDataset()` is the swap point the mock era left open, and this is the
 * swap: it fetches the FastAPI service in platform/backend over /api and maps
 * the answer onto the domain objects in data/types.ts. Nothing below that
 * mapping changed, because those types were modelled on the very tables the API
 * serves:
 *
 *   GET /api/detections   → Plume[]   (and EpisodeMember[], from episode_id)
 *   GET /api/episodes     → Episode[]
 *   GET /api/layers/aoi   → Aoi[]
 *   GET /api/layers/boundary → outline of every monitored region (drawn as is)
 *   GET /api/meta         → dataset flag, version and the list of regions
 *
 * Everything after that is unchanged:
 *  - the map / filters / stats consume GeoJSON with a small, stable set of
 *    feature properties (grade, date, satellite, …) built here;
 *  - the detail panel and export consume the raw domain objects.
 *
 * The dataset is fetched whole and filtered in the browser, as before. The API
 * can also filter server-side (bbox / from / to / sensor / min_rate) — that is
 * the upgrade path once the volume outgrows a single payload, but at the
 * current ~600 detections one request beats a request per interaction.
 *
 * There is deliberately no fallback to data/mock.ts. Synthetic detections that
 * look real are the worst failure this UI can produce, so an unreachable
 * backend surfaces as the map's error state instead of as plausible fiction.
 */
import type { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from "geojson";

import {
  gradeForRate,
  GRADE_RANK,
  type Aoi,
  type Dataset,
  type Episode,
  type EpisodeMember,
  type Plume,
  type PlumeGrade,
  type QuantificationStatus,
  type Satellite,
} from "../data/types";
import type { Locale } from "../store/localeStore";

/**
 * Same-origin by default, which vite.config.ts proxies to the API (MAP_API,
 * default http://127.0.0.1:8077). Set VITE_API_BASE for a cross-origin
 * deployment; the backend's CORS allowlist is MAP_CORS_ORIGINS.
 */
const BASE = import.meta.env.VITE_API_BASE ?? "/api";

async function get<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`);
  if (!response.ok) {
    throw new Error(`GET ${BASE}${path} -> ${response.status} ${response.statusText}`);
  }
  return (await response.json()) as T;
}

// ── Backend wire types ──────────────────────────────────────────────────────
// Mirrors of backend/methane_map/geojson.py. Kept separate from the domain
// types on purpose: the API is a contract that can drift, and the mapping below
// is where that drift has to be noticed.

interface ApiFeature<G, P> {
  type: "Feature";
  id: number;
  geometry: G;
  properties: P;
}

interface ApiCollection<G, P> {
  type: "FeatureCollection";
  count: number;
  /** The server capped the answer at queries.MAX_FEATURES. */
  truncated: boolean;
  features: ApiFeature<G, P>[];
}

interface DetectionProps {
  plume_id: number;
  episode_id: number | null;
  sensing_time: string;
  sensor: string;
  emission_rate_kg_hr: number | null;
  emission_rate_low: number | null;
  emission_rate_high: number | null;
  quantification_status: QuantificationStatus;
  quantification_variant: string;
  max_likelihood: number;
  mean_likelihood: number | null;
  area_m2: number | null;
  plume_length_m: number | null;
  n_pixels: number;
  n_saturated_pixels: number;
  ime_mol: number | null;
  wind_speed_ms: number | null;
  wind_direction_deg: number | null;
  wind_source: string | null;
  wind_time: string | null;
  source_type: string | null;
  locus: [number, number];
  /** Set when the retrieval saturated, i.e. the rate is a floor. */
  rate_is_lower_bound: boolean;
}

interface EpisodeProps {
  episode_id: number;
  gap_days: number;
  first_seen: string;
  last_seen: string;
  duration_days: number;
  n_detections: number;
  n_dates: number;
  sensors: string[];
  max_rate_kg_hr: number | null;
  mean_rate_kg_hr: number | null;
  total_ch4_kg: number | null;
  extent_km: number;
  possibly_merged: boolean;
  centroid: [number, number];
  source_type: string | null;
}

/** /api/layers/aoi is a static asset: names and categories, no ids. */
interface AoiProps {
  name: string;
  category: string;
}

/** One monitored region, as /api/meta lists them (display order). */
export interface Region {
  key: string;
  /** Russian name, kept for compatibility; prefer `names`. */
  name: string;
  /** Name per interface language, shipped with the list so a language switch needs no refetch. */
  names: Record<Locale, string>;
  /** [west, south, east, north] */
  bbox: [number, number, number, number];
}

interface ApiMeta {
  time_range: { min: string; max: string };
  counts: { detections: number; episodes: number; scenes: number };
  sensors: string[];
  dates: string[];
  regions: Region[];
  clustering: {
    radius_m: number;
    gap_days_options: number[];
    gap_days_default: number;
    gap_days: number;
    merge_warning_km: number;
  };
  data_version: string;
  dataset: "demo" | "live";
  basemap: { url: string; attribution: string };
}

// ── Wire → domain ───────────────────────────────────────────────────────────

function toPlume(f: ApiFeature<Polygon, DetectionProps>): Plume {
  const p = f.properties;
  return {
    plume_id: p.plume_id,
    sensor: p.sensor,
    sensing_time: p.sensing_time,
    geom: f.geometry,
    locus: p.locus,
    area_m2: p.area_m2,
    plume_length_m: p.plume_length_m,
    max_likelihood: p.max_likelihood,
    mean_likelihood: p.mean_likelihood,
    n_pixels: p.n_pixels,
    n_saturated_pixels: p.n_saturated_pixels,
    ime_mol: p.ime_mol,
    emission_rate_kg_hr: p.emission_rate_kg_hr,
    emission_rate_low: p.emission_rate_low,
    emission_rate_high: p.emission_rate_high,
    quantification_status: p.quantification_status,
    quantification_variant: p.quantification_variant,
    wind_speed_ms: p.wind_speed_ms,
    wind_direction_deg: p.wind_direction_deg,
    wind_source: p.wind_source,
    wind_time: p.wind_time,
    source_type: p.source_type,
  };
}

function toEpisode(f: ApiFeature<Polygon, EpisodeProps>): Episode {
  const p = f.properties;
  return {
    episode_id: p.episode_id,
    gap_days: p.gap_days,
    // Stored in map.episode but not exposed by /api/episodes, and nothing in
    // the UI reads it. Null rather than a guess.
    anchor_plume_id: null,
    first_seen: p.first_seen,
    last_seen: p.last_seen,
    duration_days: p.duration_days,
    n_detections: p.n_detections,
    n_dates: p.n_dates,
    sensors: p.sensors,
    max_rate_kg_hr: p.max_rate_kg_hr,
    mean_rate_kg_hr: p.mean_rate_kg_hr,
    total_ch4_kg: p.total_ch4_kg,
    centroid: p.centroid,
    // The episode's geometry is its convex hull (queries.py: ST_AsGeoJSON(e.hull)).
    hull: f.geometry,
    extent_km: p.extent_km,
    possibly_merged: p.possibly_merged,
  };
}

/** Centroid of a polygon's outer ring, by the shoelace formula. */
function polygonCentroid(poly: Polygon): [number, number] {
  const ring = poly.coordinates[0];
  let twiceArea = 0;
  let x = 0;
  let y = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const cross = xj * yi - xi * yj;
    twiceArea += cross;
    x += (xj + xi) * cross;
    y += (yj + yi) * cross;
  }
  // A degenerate ring encloses no area and would divide by zero. Its first
  // vertex is as representative a point as any.
  if (twiceArea === 0) return [ring[0][0], ring[0][1]];
  return [x / (3 * twiceArea), y / (3 * twiceArea)];
}

/**
 * One source per contiguous zone.
 *
 * The AOI layer is merged per category upstream: four features, three of them
 * MultiPolygons whose parts are scattered across the country (kz_oil_gas alone
 * has 15). A single marker at the centroid of such a feature would sit in empty
 * steppe hundreds of kilometres from every part of it, so each part becomes its
 * own source. Parts keep the parent's name with an index so a marker stays
 * traceable to the feature it came from — the layer has no per-facility names
 * to offer, because public.aoi is empty and the geometry is a category-wide
 * buffer around OSM features rather than a facility inventory.
 */
function toAois(fc: ApiCollection<Polygon | MultiPolygon, AoiProps>): Aoi[] {
  const aois: Aoi[] = [];

  for (const f of fc.features) {
    const parts: Polygon[] =
      f.geometry.type === "Polygon"
        ? [f.geometry]
        : f.geometry.coordinates.map((coordinates) => ({ type: "Polygon", coordinates }));

    parts.forEach((geom, i) => {
      aois.push({
        // The asset carries no ids; position in the exploded layer is the only
        // stable handle, and it is what selection round-trips through the URL.
        aoi_id: aois.length + 1,
        name: parts.length > 1 ? `${f.properties.name} #${i + 1}` : f.properties.name,
        category: f.properties.category,
        geom,
        centroid: polygonCentroid(geom),
      });
    });
  }

  return aois;
}

// ── Loading ─────────────────────────────────────────────────────────────────

async function fetchDataset(): Promise<Dataset> {
  // Ask the server which clustering it considers default rather than naming a
  // number here. It is 60 days, argued in the backend's SPEC 4.3: the observed
  // gap between usable acquisitions of one leak is ~41 days, so 30 shatters a
  // months-long event into unrelated pieces. The UI has no selector yet; the
  // supported values are in meta.clustering.gap_days_options.
  const { clustering } = await loadApiMeta();
  const gapDays = clustering.gap_days_default;

  const [detections, episodes, aoi] = await Promise.all([
    get<ApiCollection<Polygon, DetectionProps>>(`/detections?gap_days=${gapDays}`),
    get<ApiCollection<Polygon, EpisodeProps>>(`/episodes?gap_days=${gapDays}`),
    get<ApiCollection<Polygon | MultiPolygon, AoiProps>>("/layers/aoi"),
  ]);

  // Silence here would mean a map that quietly shows part of the country.
  if (detections.truncated || episodes.truncated) {
    console.warn(
      "[api] the backend capped its answer (queries.MAX_FEATURES); " +
        "the map is showing a subset of the data",
    );
  }

  // map.episode_member is not exposed as an endpoint, but each detection
  // carries its episode_id, which is the same link.
  const members: EpisodeMember[] = [];
  for (const f of detections.features) {
    const episodeId = f.properties.episode_id;
    if (episodeId !== null) {
      members.push({ episode_id: episodeId, plume_id: f.properties.plume_id });
    }
  }

  return {
    plumes: detections.features.map(toPlume),
    episodes: episodes.features.map(toEpisode),
    members,
    aois: toAois(aoi),
  };
}

// Cache the promises (not the results) so the concurrent api.* calls the map
// fires on mount share one round trip each.
let datasetPromise: Promise<Dataset> | null = null;
function loadDataset(): Promise<Dataset> {
  if (!datasetPromise) datasetPromise = fetchDataset();
  return datasetPromise;
}

// Asked for without a gap_days parameter, so the answer reports the server's
// own default rather than echoing one back at us.
let metaPromise: Promise<ApiMeta> | null = null;
function loadApiMeta(): Promise<ApiMeta> {
  if (!metaPromise) metaPromise = get<ApiMeta>("/meta");
  return metaPromise;
}

/**
 * Normalize the free-text `sensor` into a filter key.
 * Verified against the live database on 2026-07-31: the only value present is
 * "sentinel-2". The other branches stand ready for the Landsat and EMIT
 * ingests; extend this and SATELLITE_OPTIONS together if new values appear.
 */
export function normalizeSensor(sensor: string): Satellite {
  const s = sensor.toLowerCase();
  if (s.includes("sentinel")) return "sentinel-2";
  if (s.includes("landsat") && s.includes("9")) return "landsat-9";
  if (s.includes("landsat")) return "landsat-8";
  if (s.includes("emit")) return "emit";
  return "sentinel-2";
}

// ── Types shared with the UI ────────────────────────────────────────────────

export interface Meta {
  dateRange: { min: string; max: string };
  dates: string[];
  sensors: Satellite[];
  counts: { detections: number; episodes: number; sources: number };
  /**
   * "demo" means the backend is serving synthetic detections (MAP_DATASET).
   * Drives the ДЕМО badge in the header.
   */
  dataset: "demo" | "live";
  /** Changes when the pipeline writes new detections (see the /ws socket). */
  dataVersion: string;
  /**
   * Monitored regions, in the backend's display order. The first is the
   * default extent; the region control flies to the others.
   */
  regions: Region[];
}

/** Stable plume feature properties the map/filters/stats read. */
export interface PlumeProps {
  detectionId: string;
  episodeId: string;
  date: string;
  emissionRate: number | null;
  grade: PlumeGrade;
  satellite: Satellite;
}

/** Stable source (AOI) feature properties. */
export interface SourceProps {
  sourceId: string;
  name: string;
  category: string;
  grades: PlumeGrade[];
  maxGrade: PlumeGrade;
}

// ── Derived indexes over the dataset ────────────────────────────────────────

function index(ds: Dataset) {
  const episodeByPlume = new Map<number, number>();
  for (const m of ds.members) episodeByPlume.set(m.plume_id, m.episode_id);
  const plumesByEpisode = new Map<number, Plume[]>();
  const plumeById = new Map(ds.plumes.map((p) => [p.plume_id, p]));
  for (const m of ds.members) {
    const p = plumeById.get(m.plume_id);
    if (!p) continue;
    const list = plumesByEpisode.get(m.episode_id) ?? [];
    list.push(p);
    plumesByEpisode.set(m.episode_id, list);
  }
  return { episodeByPlume, plumesByEpisode, plumeById };
}

/** Ray-casting point-in-polygon over a Polygon's outer ring. */
function pointInPolygon([x, y]: [number, number], poly: Polygon): boolean {
  const ring = poly.coordinates[0];
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Bounding box of a polygon's outer ring, as [minX, minY, maxX, maxY]. */
function polygonBbox(poly: Polygon): [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of poly.coordinates[0]) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

/** For each AOI, the plume grades whose locus falls inside it. */
function gradesByAoi(ds: Dataset): Map<number, Set<PlumeGrade>> {
  const map = new Map<number, Set<PlumeGrade>>();
  // The live AOI layer explodes into a few hundred zones and every plume is
  // tested against every one of them. Rejecting by bounding box first turns
  // almost all of those tests into four comparisons instead of a full scan of
  // a buffered OSM cluster's ring.
  for (const aoi of ds.aois) {
    const [minX, minY, maxX, maxY] = polygonBbox(aoi.geom);
    const set = new Set<PlumeGrade>();
    for (const p of ds.plumes) {
      const [x, y] = p.locus;
      if (x < minX || x > maxX || y < minY || y > maxY) continue;
      if (pointInPolygon(p.locus, aoi.geom)) set.add(gradeForRate(p.emission_rate_kg_hr));
    }
    map.set(aoi.aoi_id, set);
  }
  return map;
}

function worstGrade(grades: Iterable<PlumeGrade>): PlumeGrade {
  let worst: PlumeGrade = "unknown";
  for (const g of grades) if (GRADE_RANK[g] > GRADE_RANK[worst]) worst = g;
  return worst;
}

/** The AOI whose polygon contains a point, if any. */
function aoiContaining(ds: Dataset, point: [number, number]): Aoi | null {
  return ds.aois.find((a) => pointInPolygon(point, a.geom)) ?? null;
}

// ── GeoJSON builders (map layers) ───────────────────────────────────────────

function plumesGeoJSON(ds: Dataset): FeatureCollection<Polygon, PlumeProps> {
  const { episodeByPlume } = index(ds);
  const features: Feature<Polygon, PlumeProps>[] = ds.plumes.map((p, i) => {
    const episodeId = episodeByPlume.get(p.plume_id);
    return {
      type: "Feature",
      id: i + 1,
      geometry: p.geom,
      properties: {
        detectionId: String(p.plume_id),
        episodeId: episodeId !== undefined ? String(episodeId) : "",
        date: p.sensing_time.slice(0, 10),
        emissionRate: p.emission_rate_kg_hr,
        grade: gradeForRate(p.emission_rate_kg_hr),
        satellite: normalizeSensor(p.sensor),
      },
    };
  });
  return { type: "FeatureCollection", features };
}

// The source markers and the AOI polygons carry the same properties, and the
// scan behind them is the most expensive thing in this module. One dataset, one
// computation.
const sourcePropsCache = new WeakMap<Dataset, Map<number, SourceProps>>();

function aoiSourceProps(ds: Dataset): Map<number, SourceProps> {
  const cached = sourcePropsCache.get(ds);
  if (cached) return cached;

  const grades = gradesByAoi(ds);
  const map = new Map<number, SourceProps>();
  for (const a of ds.aois) {
    const set = grades.get(a.aoi_id) ?? new Set<PlumeGrade>();
    map.set(a.aoi_id, {
      sourceId: String(a.aoi_id),
      name: a.name,
      category: a.category,
      grades: [...set],
      maxGrade: worstGrade(set),
    });
  }

  sourcePropsCache.set(ds, map);
  return map;
}

function sourcesGeoJSON(ds: Dataset): FeatureCollection<Point, SourceProps> {
  const props = aoiSourceProps(ds);
  const features: Feature<Point, SourceProps>[] = ds.aois.map((a, i) => ({
    type: "Feature",
    id: i + 1,
    geometry: { type: "Point", coordinates: a.centroid },
    properties: props.get(a.aoi_id)!,
  }));
  return { type: "FeatureCollection", features };
}

function aoiGeoJSON(ds: Dataset): FeatureCollection<Polygon, SourceProps> {
  const props = aoiSourceProps(ds);
  const features: Feature<Polygon, SourceProps>[] = ds.aois.map((a, i) => ({
    type: "Feature",
    id: i + 1,
    geometry: a.geom,
    properties: props.get(a.aoi_id)!,
  }));
  return { type: "FeatureCollection", features };
}

function buildMeta(ds: Dataset, apiMeta: ApiMeta): Meta {
  // Dates and counts are derived from what was actually loaded, not from
  // /api/meta: if the answer was capped, the timeline must span the data on the
  // map rather than a range that has nothing to show at its edges.
  const dates = [...new Set(ds.plumes.map((p) => p.sensing_time.slice(0, 10)))].sort();
  const sensors = [...new Set(ds.plumes.map((p) => normalizeSensor(p.sensor)))];
  return {
    dateRange: { min: dates[0] ?? "", max: dates[dates.length - 1] ?? "" },
    dates,
    sensors,
    counts: {
      detections: ds.plumes.length,
      episodes: ds.episodes.length,
      sources: ds.aois.length,
    },
    dataset: apiMeta.dataset,
    dataVersion: apiMeta.data_version,
    regions: apiMeta.regions,
  };
}

// ── Public API ──────────────────────────────────────────────────────────────

export const api = {
  getMeta: async (): Promise<Meta> => {
    const [ds, apiMeta] = await Promise.all([loadDataset(), loadApiMeta()]);
    return buildMeta(ds, apiMeta);
  },

  getPlumesGeoJSON: async () => plumesGeoJSON(await loadDataset()),
  getSourcesGeoJSON: async () => sourcesGeoJSON(await loadDataset()),
  getAoiGeoJSON: async () => aoiGeoJSON(await loadDataset()),
  /** Outlines of the monitored regions; properties carry key, name and bbox. */
  getBoundaryGeoJSON: () =>
    get<FeatureCollection<Polygon | MultiPolygon, Region>>("/layers/boundary"),

  // Raw domain objects for export / detail.
  getPlumes: async (): Promise<Plume[]> => (await loadDataset()).plumes,
  getEpisodes: async (): Promise<Episode[]> => (await loadDataset()).episodes,
  getAois: async (): Promise<Aoi[]> => (await loadDataset()).aois,
  getMembers: async () => (await loadDataset()).members,

  /** Episode detail: the episode, its plumes (sorted), and the containing AOI. */
  getEpisode: async (
    id: string,
  ): Promise<{ episode: Episode; plumes: Plume[]; aoi: Aoi | null } | null> => {
    const ds = await loadDataset();
    const episodeId = Number(id);
    const episode = ds.episodes.find((e) => e.episode_id === episodeId);
    if (!episode) return null;
    const { plumesByEpisode } = index(ds);
    const plumes = (plumesByEpisode.get(episodeId) ?? [])
      .slice()
      .sort((a, b) => a.sensing_time.localeCompare(b.sensing_time));
    return { episode, plumes, aoi: aoiContaining(ds, episode.centroid) };
  },

  /** AOI detail: the AOI and the episodes whose centroid falls inside it. */
  getSource: async (id: string): Promise<{ aoi: Aoi; episodes: Episode[] } | null> => {
    const ds = await loadDataset();
    const aoi = ds.aois.find((a) => a.aoi_id === Number(id));
    if (!aoi) return null;
    const episodes = ds.episodes
      .filter((e) => pointInPolygon(e.centroid, aoi.geom))
      .sort((a, b) => b.first_seen.localeCompare(a.first_seen));
    return { aoi, episodes };
  },
};
